import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useMutation, useLazyQuery } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';
import Button from './Button';
import colors from '../styles/colors';
import { PASSWORD_LOGIN, GET_USER_ROLES, GET_CURRENT_USER } from '../graphql/queries';
import { getPostLoginPath } from '../util/postLoginRedirect';
import { getErrorMessage } from '../util/ErrorUtil';
import Error from './Error';
import Input from './Input';
import { Flex } from './Flex';
import Logo from '../assets/images/cto-full-logo.png';
import { media } from '../util/MediaQuery';
import Loader from './Loader';

const Form = styled.form`
  align-items: center;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: ${colors.white};
  position: relative;
  border-radius: 5px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  margin: auto;
  overflow-y: auto;
  ${media.desktop`
    &::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }
  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 10px;
  }
    padding: 30px;
    width: 400px;
  `};

  ${media.mobile`
    height: 100vh;
    width: 100vw;
    padding: 10px;
  `};
  ${media.desktop`
    min-height: 85vh !important;
    max-height: 85vh !important;
  `};
  ${media.tablet`
    min-height: 100vh;
    max-height: 100vh;
  `};
  display: grid;
  width: 100%;
  grid-template-columns: 1fr;
`;

const Content = styled.div`
  align-items: center;
  justify-content: center;
`;

const InnerContainer = styled.div`
  align-items: center;
  justify-content: center;
`;

const BottomText = styled.div`
  font-size: 1.2rem;
  color: ${(props) => props.color || `${colors.grey3}`};
  cursor: ${(props) => (props.onClick ? 'pointer' : null)};
`;

const LogoImage = styled.img`
  width: 200px;
  margin: 20px 0px;
  position: relative; ;
`;

const ForgotPasswordText = styled.div`
  margin-top: 15px;
  font-size: 1rem;
  color: ${colors.seaGreen};
  align-self: baseline;
  cursor: pointer;
`;

const Spacer = styled.div`
  height: 5px;
`;
const ImageContainer = styled.div`
  max-height: 15vh;
  justify-content: center;
  display: flex;
  @media screen and (max-height: 480px) {
    max-height: 150px;
  }
`;

const LoaderContainer = styled.div`
  min-height: 100vh;
  max-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

function Login() {
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [passwordLogin, { loading: loginLoading }] = useMutation(PASSWORD_LOGIN, {
    variables: {
      email,
      password,
    },
    onError(error) {
      setErrorMsg(getErrorMessage(error));
    },
    onCompleted(data) {
      if (data && data.passwordLogin && data.passwordLogin.token) {
        localStorage.setItem(
          'CHECK_THIS_OUT_AUTH_TOKEN',
          data.passwordLogin.token,
        );
        getUserRoles();
      } else {
        setErrorMsg('Something went wrong, please contact support');
      }
    },
  });
  const [getUserRoles, { data: rolesData, loading: rolesLoading }] = useLazyQuery(GET_USER_ROLES, {
    fetchPolicy: 'no-cache',
  });
  const [getCurrentUserForRoute, { data: routeUserData, loading: routeUserLoading }] = useLazyQuery(
    GET_CURRENT_USER,
    { fetchPolicy: 'no-cache' },
  );

  useEffect(() => {
    if (rolesData?.getUserRoles) {
      const invites = rolesData.getUserRoles.filter(
        (r) => !r.acceptedAt || r.acceptedAt === 0,
      );

      if (invites.length > 0) {
        history.push({
          pathname: '/onboarding',
          state: { roles: invites },
        });
      } else {
        getCurrentUserForRoute();
      }
    }
  }, [rolesData, history, getCurrentUserForRoute]);

  useEffect(() => {
    if (!routeUserData?.getCurrentUser) return;
    const path = getPostLoginPath(routeUserData.getCurrentUser.currentOrganizationRole);
    history.push(path);
  }, [routeUserData, history]);

  useEffect(() => {
    localStorage.removeItem('CHECK_THIS_OUT_AUTH_TOKEN');
  }, []);

  const isLoading = loginLoading || rolesLoading || routeUserLoading;
  return (
    <Content>
      {isLoading ? (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      ) : (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            passwordLogin();
          }}
        >
          <InnerContainer>
            <ImageContainer>
              <LogoImage src={Logo} />
            </ImageContainer>
            <Input
              header="EMAIL"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              width="100%"
            />
            <Input
              header="PASSWORD"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              width="100%"
            />
            <ForgotPasswordText onClick={() => history.push('/forgotPassword')}>
              FORGOT PASSWORD?
            </ForgotPasswordText>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                margin="40px 0px"
                width="30%"
                text="SIGN IN"
                type="submit"
                padding="0px"
                loading={isLoading}
              />
            </div>
            <Flex align="center" direction="column" margin="0px 0px 25px 0px">
              <BottomText>DON'T HAVE AN ORGANIZATION ACCOUNT?</BottomText>
              <Spacer />
              <BottomText
                color={colors.seaGreen}
                onClick={() => history.push('/new-account/step/1')}
              >
                &nbsp;&nbsp;&nbsp;SIGN UP
              </BottomText>
            </Flex>
            <Flex align="center" direction="column">
              <BottomText>DON'T WORK FOR AN ORGANIZATION?</BottomText>
              <Spacer />
              <BottomText
                color={colors.seaGreen}
                onClick={() => window.location.assign(`${window.location.origin}/user/home`)}
              >
                CHECK YOUR USER BALANCE HERE
              </BottomText>
            </Flex>
            {errorMsg && <Error error={errorMsg} margin="20px 0px 0px 0px" />}
          </InnerContainer>
        </Form>
      )}
    </Content>
  );
}

export default Login;
